/**
 * RM（アプリ内アンケート）の行判定・payload 解析・回答状態の純関数。
 *
 * varuna は RM を RT 1 行として返し、設問を additional_dialog_info.rmSurvey に載せる
 * （buildRtDialogInfo が展開するので、ダイアログでは dialogInfo.rmSurvey で読む）。
 * 正本: varuna `.claude/reports/rm-survey-data-model-claude__focused-mccarthy-1gllol.md`
 */

export const RM_SURVEY_DIALOG_TYPE = 'open-rm-survey-dialog'
const RM_SURVEY_DIALOG_NAME = 'rm_survey'

type RmAnswerType = 'single' | 'multiple'

interface RmSurveyOption {
  code: string
  label: string
  exclusive: boolean
  freeText: boolean
}

interface RmAnswerRule {
  questionCode: string
  optionCodes: string[]
}

export interface RmSurveyQuestion {
  questionId: number
  questionCode: string
  stepNum: number
  questionText: string
  answerType: RmAnswerType
  options: RmSurveyOption[]
  showIf: RmAnswerRule | null
}

export interface RmSurveyDialogContent {
  title: string
  nextLabel: string
  submitLabel: string
  closeLabel: string
  /** 回答画面下部の注記（プライバシーポリシーへのリンクを添える） */
  notice: string
  privacyUrl: string
  resultTitle: string
  resultBody: string
  /** 調査結果ページ。空なら「調査結果を見る」ボタンを出さない */
  resultUrl: string
  resultLabel: string
}

export interface RmSurvey {
  rmConditionId: number
  rmCode: string
  dialogContent: RmSurveyDialogContent
  questions: RmSurveyQuestion[]
}

/** 回答の途中状態。question_code → 選んだ option code（選んだ順） */
export type RmSelections = Record<string, string[]>

/** dialog_content の既定文言（キー: snake_case。無いキーはここを使う） */
const DEFAULT_CONTENT: RmSurveyDialogContent = {
  title: '1分アンケートにご協力ください',
  nextLabel: '次へ',
  submitLabel: '回答する',
  closeLabel: '回答しない',
  notice: '回答は個人が特定されない統計データとしてWebで公開します。',
  privacyUrl: 'https://www.yagish.jp/privacy_policy/',
  resultTitle: '集計結果はWebで公開します',
  resultBody:
    'いただいた回答は統計データとしてまとめ、ヤギディアで公開します。個人が特定される形で公開されることはありません。',
  resultUrl: '',
  resultLabel: 'ヤギディアで調査結果を見る'
}

const CONTENT_KEYS: Record<keyof RmSurveyDialogContent, string> = {
  title: 'title',
  nextLabel: 'next_label',
  submitLabel: 'submit_label',
  closeLabel: 'close_label',
  notice: 'notice',
  privacyUrl: 'privacy_url',
  resultTitle: 'result_title',
  resultBody: 'result_body',
  resultUrl: 'result_url',
  resultLabel: 'result_label'
}

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toInt(value: unknown): number | null {
  const n = typeof value === 'string' ? Number(value) : value
  return typeof n === 'number' && Number.isInteger(n) ? n : null
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function isRmSurveyConditionRow(row: unknown): boolean {
  if (!isRecord(row)) return false
  return (
    row.dialog_type === RM_SURVEY_DIALOG_TYPE ||
    row.dialog_name === RM_SURVEY_DIALOG_NAME
  )
}

function parseRule(value: unknown): RmAnswerRule | null {
  if (!isRecord(value)) return null
  const questionCode = toText(value.question_code)
  const optionCodes = Array.isArray(value.option_codes)
    ? value.option_codes.filter(
        (code): code is string => typeof code === 'string' && code !== ''
      )
    : []
  if (!questionCode || optionCodes.length === 0) return null
  return { questionCode, optionCodes }
}

function parseOption(value: unknown): RmSurveyOption | null {
  if (!isRecord(value)) return null
  const code = toText(value.code)
  if (!code) return null
  return {
    code,
    label: toText(value.label),
    exclusive: value.exclusive === true,
    freeText: value.free_text === true
  }
}

function parseQuestion(value: unknown): RmSurveyQuestion | null {
  if (!isRecord(value)) return null
  const questionId = toInt(value.question_id)
  const stepNum = toInt(value.step_num)
  const answerType = value.answer_type
  if (questionId == null || stepNum == null) return null
  if (answerType !== 'single' && answerType !== 'multiple') return null
  const options = (Array.isArray(value.options) ? value.options : [])
    .map(parseOption)
    .filter((opt): opt is RmSurveyOption => opt !== null)
  if (options.length === 0) return null
  return {
    questionId,
    questionCode: toText(value.question_code),
    stepNum,
    questionText: toText(value.question_text),
    answerType,
    options,
    showIf: parseRule(value.show_if)
  }
}

function parseContent(value: unknown): RmSurveyDialogContent {
  const raw = isRecord(value) ? value : {}
  const content = { ...DEFAULT_CONTENT }
  for (const key of Object.keys(CONTENT_KEYS) as Array<
    keyof RmSurveyDialogContent
  >) {
    const text = toText(raw[CONTENT_KEYS[key]])
    if (text) content[key] = text
  }
  return content
}

/** dialogInfo.rmSurvey（= additional_dialog_info.rmSurvey）を型付きに変換する。壊れていれば null */
export function parseRmSurvey(value: unknown): RmSurvey | null {
  if (!isRecord(value)) return null
  const rmConditionId = toInt(value.rm_condition_id)
  if (rmConditionId == null) return null
  const rawQuestions = Array.isArray(value.questions) ? value.questions : []
  const questions = rawQuestions.map(parseQuestion)
  // 1 問でも壊れていたらポップアップごと出さない（分岐の前提が崩れるため）
  if (questions.length === 0 || questions.some((q) => q === null)) return null
  return {
    rmConditionId,
    rmCode: toText(value.rm_code),
    dialogContent: parseContent(value.dialog_content),
    questions: (questions as RmSurveyQuestion[])
      .slice()
      .sort((a, b) => a.stepNum - b.stepNum)
  }
}

function matchesRule(
  rule: RmAnswerRule | null,
  selections: RmSelections
): boolean {
  if (!rule) return true
  const selected = selections[rule.questionCode] || []
  return selected.some((code) => rule.optionCodes.includes(code))
}

/** show_if を満たす設問だけを表示順に返す */
function visibleQuestions(
  survey: RmSurvey,
  selections: RmSelections
): RmSurveyQuestion[] {
  return survey.questions.filter((q) => matchesRule(q.showIf, selections))
}

/**
 * 選択を 1 つ切り替えた後の選択肢。
 * 単一選択は置き換え。複数選択で exclusive を選んだら他を外し、他を選んだら exclusive を外す。
 */
export function toggleSelection(
  question: RmSurveyQuestion,
  current: string[],
  code: string
): string[] {
  const option = question.options.find((opt) => opt.code === code)
  if (!option) return current
  if (question.answerType === 'single') return [code]
  if (current.includes(code)) return current.filter((c) => c !== code)
  if (option.exclusive) return [code]
  const exclusiveCodes = new Set(
    question.options.filter((opt) => opt.exclusive).map((opt) => opt.code)
  )
  return [...current.filter((c) => !exclusiveCodes.has(c)), code]
}

export function needsFreeText(
  question: RmSurveyQuestion,
  selected: string[]
): boolean {
  return question.options.some(
    (opt) => opt.freeText && selected.includes(opt.code)
  )
}

interface RmAnswerInput {
  question_id: number
  option_codes: string[]
  free_text: string
}

/** submitRmAnswer の answers。表示中の設問だけを送る（分岐で隠れた設問は送らない） */
export function buildAnswers(
  survey: RmSurvey,
  selections: RmSelections,
  freeTexts: Record<string, string>
): RmAnswerInput[] {
  return visibleQuestions(survey, selections).map((q) => {
    const selected = selections[q.questionCode] || []
    return {
      question_id: q.questionId,
      option_codes: selected,
      free_text: needsFreeText(q, selected)
        ? (freeTexts[q.questionCode] || '').trim()
        : ''
    }
  })
}

interface RmSurveyStep {
  question: RmSurveyQuestion
  /** 1 始まりの番号 */
  number: number
  total: number
  isLast: boolean
}

/**
 * 1 問ずつ出すときの現在の設問。分岐（show_if）で総数が変わるので毎回数え直す。
 * index が範囲外なら最後の設問に寄せる。
 */
export function surveyStep(
  survey: RmSurvey,
  selections: RmSelections,
  index: number
): RmSurveyStep {
  const visible = visibleQuestions(survey, selections)
  const i = Math.min(Math.max(index, 0), visible.length - 1)
  return {
    question: visible[i],
    number: i + 1,
    total: visible.length,
    isLast: i === visible.length - 1
  }
}

/** varuna の device_flag と同じ 'SP' / 'PC'（plugins/device-lib.js の isMobileDevice と同じ判定） */
export function resolveDeviceFlag(userAgent: string): 'SP' | 'PC' {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
    userAgent
  )
    ? 'SP'
    : 'PC'
}
